/**
 * JWT utility functions extracted from existing auth services
 */

/**
 * Parse JWT payload safely
 */
export function parseJwtPayload(
  jwt: string | undefined,
): Record<string, any> | undefined {
  if (!jwt) {
    return undefined;
  }

  const parts = jwt.split('.');
  if (parts.length !== 3 || !parts[1]) {
    console.error(
      `Invalid JWT format - expected 3 parts (header, payload, signature), got ${parts.length}`,
    );
    return undefined;
  }

  const payload = parts[1];
  // Replace - with + and _ with / to handle URL-safe base64
  const base64 = payload.replaceAll('-', '+').replaceAll('_', '/');

  try {
    return JSON.parse(decodeURIComponent(escape(globalThis.atob(base64))));
  } catch (error) {
    console.error('Failed to decode JWT payload:', error);
    return undefined;
  }
}

/**
 * Check if JWT token is expired
 */
export function isJwtExpired(jwt: string | undefined): boolean {
  if (!jwt) {
    return true;
  }

  const payload = parseJwtPayload(jwt);
  if (!payload?.exp) {
    return true;
  }

  return payload.exp * 1000 <= Date.now();
}

/**
 * Check if JWT token is valid (not expired)
 */
export function isJwtValid(jwt: string | undefined): boolean {
  return !isJwtExpired(jwt);
}

/**
 * Get user ID from JWT token
 */
export function getUserIdFromJwt(jwt: string | undefined): string | undefined {
  const payload = parseJwtPayload(jwt);
  return payload?.sub;
}

/**
 * Extract the HTTP status code from an error thrown by an HttpClient
 * implementation (ky HTTPError and axios errors both expose error.response.status)
 */
export function getHttpErrorStatus(error: unknown): number | undefined {
  const status = (error as { response?: { status?: unknown } } | undefined)
    ?.response?.status;

  return typeof status === 'number' ? status : undefined;
}
